import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MetaTags from '@/components/meta/MetaTags';
import BulletinsHeader from '@/components/bulletins/BulletinsHeader';
import BulletinsTabs from '@/components/bulletins/BulletinsTabs';
import { useInfiniteBulletins } from '@/hooks/use-infinite-bulletins';
import BulletinsGrid from '@/components/bulletins/BulletinsGrid';
import BulletinDetailView from '@/components/bulletins/BulletinDetailView';
import BulletinLightbox from '@/components/bulletins/BulletinLightbox';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

const Chrono: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { bulletinId } = useParams();
  const queryClient = useQueryClient();

  // Measure content area to respect left navigation on desktop
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentBounds, setContentBounds] = useState<{
    top: number; left: number; width: number; height: number;
  } | null>(null);
  const updateContentBounds = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setContentBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);
  useEffect(() => {
    updateContentBounds();
    window.addEventListener('resize', updateContentBounds);
    return () => window.removeEventListener('resize', updateContentBounds);
  }, [updateContentBounds, isMobile]);

  const activeTab: 'pour-toi' | 'top-follows' | 'updates' = 'pour-toi';
  const activeCategory = 'chrono';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteBulletins(activeTab, activeCategory);

  const bulletins = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  const selected = useMemo(() => bulletins.find(b => b.id === bulletinId) || null, [bulletins, bulletinId]);

  const handleLike = async (id: string) => {
    queryClient.setQueryData(['infinite-bulletins', activeTab], (oldData: any) => {
      if (!oldData?.pages?.length) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((item: any) => item.id === id ? { ...item, like_count: (item.like_count || 0) + 1 } : item)
        }))
      };
    });
  };

  const handleDelete = () => {
    queryClient.invalidateQueries({ queryKey: ['infinite-bulletins', activeTab], exact: false });
  };

  const handleBulletinClick = (id: string) => {
    navigate(`/chrono/${id}`);
  };

  const handleClose = () => {
    navigate('/chrono');
  };

  return (
    <div>
      <MetaTags 
        title="Chrono – Latest community posts"
        description="See the latest posts in chronological order. Fresh content, every time you load."
        image="/og-image.png"
        url={window.location.href}
      />

      <div ref={contentRef} className="container max-w-7xl mx-auto pt-25 px-4 py-[22px]">
        <motion.div 
          initial={{ y: -20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2, duration: isMobile ? 0.2 : 0.3 }} 
          className="mx-0"
        >
          <BulletinsHeader activeTab={activeTab} activeCategory={activeCategory} queuedCount={0} />
        </motion.div>

        <motion.div 
          initial={{ y: -10, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.3, duration: isMobile ? 0.1 : 0.2 }} 
          className="mb-6"
        >
          <BulletinsTabs activeTab={activeTab} onTabChange={() => {}} activeCategory={activeCategory} showTabs={true} />
        </motion.div>

        {isLoading && bulletins.length === 0 ? (
          <div className="flex justify-center my-12"><LoadingFallback /></div>
        ) : (
          <BulletinsGrid 
            bulletins={bulletins}
            onLike={handleLike}
            onDelete={handleDelete}
            activeTab={activeTab}
            onLoadMore={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            onBulletinClick={handleBulletinClick}
            selectedBulletinId={bulletinId}
          />
        )}

        <AnimatePresence>
          {selected && (
            <BulletinLightbox
              bulletins={bulletins}
              selectedId={selected.id}
              onNavigate={(id) => handleBulletinClick(id)}
              onClose={handleClose}
              contentBounds={!isMobile ? contentBounds : null}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Chrono;
